/* eslint-disable max-len */
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

interface PermissionLabels {
	create: string;
	view: string;
	edit: string;
	publish: string;
	unpublish: string;
	delete: string;
}

interface ContentTypePermissions {
	articles: boolean;
	videos: boolean;
	podcasts: boolean;
	documents: boolean;
	images: boolean;
	categories: boolean;
	playlists: boolean;
	seasons: boolean;
	users?: boolean;
}
interface EmployeePermissions {
	employeeId: string;
	name: string;
	role: string;
	permissions: {
		create: boolean;
		view: boolean;
		edit: boolean;
		publish: boolean;
	};
	updatedAt: string;
	_id: string;
}

const useContentPermissions = () =>
{
	const contentManagement = useSelector((state: any) => state?.mysession?.configs?.contentManagement);
	const employeePermissions: EmployeePermissions = useSelector((state: any) => state?.mysession?.userConfigs?.employeePermissions);

	// const userData = useSelector((state: any) => state?.mysession?.session);

	// Check if data is still loading
	const isDataLoading = contentManagement === undefined || contentManagement === null;
	const isEmployeePermissionsLoading = employeePermissions === undefined || employeePermissions === null;

	// Check if resources are enabled
	const isResourcesEnabled = isDataLoading ? true : (contentManagement?.resources?.enabled || false);

	// Get enabled modules from configuration
	const isArticlesEnabled = isDataLoading ? true : ((isResourcesEnabled && contentManagement?.modulesEnabled?.articles) || false);
	const isImagesEnabled = isDataLoading ? true : ((isResourcesEnabled && contentManagement?.modulesEnabled?.images) || false);
	const isVideosEnabled = isDataLoading ? true : ((isResourcesEnabled && contentManagement?.modulesEnabled?.videos) || false);
	const isDocumentsEnabled = isDataLoading ? true : ((isResourcesEnabled && contentManagement?.modulesEnabled?.documents) || false);
	const isPodcastsEnabled = isDataLoading ? true : ((isResourcesEnabled && contentManagement?.modulesEnabled?.podcasts) || false);
	const isPlaylistsEnabled = isDataLoading ? true : ((isResourcesEnabled && contentManagement?.creationFeatures?.playlist) || false);
	const isSeasonsEnabled = isDataLoading ? true : ((isResourcesEnabled && contentManagement?.creationFeatures?.season) || false);

	// Check if all content types are disabled
	const isAllContentDisabled = isDataLoading ? false : (!isArticlesEnabled && !isImagesEnabled && !isVideosEnabled && !isDocumentsEnabled && !isPodcastsEnabled && !isPlaylistsEnabled);

	const isLocalUploadEnabled = isDataLoading ? true : (contentManagement?.uploadSettings?.sources?.local || false);
	const isEmbedUploadEnabled = isDataLoading ? true : (contentManagement?.uploadSettings?.sources?.embeddedLink || false);
	const isDriveUploadEnabled = isDataLoading ? true : (contentManagement?.uploadSettings?.sources?.mediaDrive || false);
	const isResourceUploadEnabled = isDataLoading ? true : (contentManagement?.uploadSettings?.sources?.resources || false);

	const isDownloadEnabled = isDataLoading ? true : (contentManagement?.accessSettings?.download || false);
	const isShareEnabled = isDataLoading ? true : (contentManagement?.accessSettings?.share || false);
	const isPublicShareEnabled = isDataLoading ? true : (contentManagement?.accessSettings?.public || false);
	const isPrivateShareEnabled = isDataLoading ? true : (contentManagement?.accessSettings?.private || false);

	const uploadSizeMBLimit = isDataLoading ? true : contentManagement?.uploadSettings?.maxFileSizeMB;

	const hasViewPermission = isEmployeePermissionsLoading ? true : (employeePermissions?.permissions?.view || false);
	const hasCreatePermission = isEmployeePermissionsLoading ? true : (employeePermissions?.permissions?.create || false);
	const hasEditPermission = isEmployeePermissionsLoading ? true : (employeePermissions?.permissions?.edit || false);
	const hasPublishPermission = isEmployeePermissionsLoading ? false : (employeePermissions?.permissions?.publish || false);

	return {
		contentManagement,
		isDataLoading,

		isArticlesEnabled,
		isImagesEnabled,
		isVideosEnabled,
		isDocumentsEnabled,
		isPodcastsEnabled,
		isPlaylistsEnabled,
		isSeasonsEnabled,
		isResourcesEnabled,
		isAllContentDisabled,

		isLocalUploadEnabled,
		isEmbedUploadEnabled,
		isDriveUploadEnabled,
		isResourceUploadEnabled,

		isDownloadEnabled,
		isShareEnabled,
		isPublicShareEnabled,
		isPrivateShareEnabled,

		uploadSizeMBLimit,

		// individual user permissions
		hasViewPermission,
		hasCreatePermission,
		hasEditPermission,
		hasPublishPermission
	};
};

export default useContentPermissions;